// Buffered relational-edit state machine — converts user actions on an
// embedded O2M / M2M list (Add Line · Edit · Remove) into a list of
// `PendingOp` entries, then serialises that list to the Odoo-compatible
// relational-command tuple format that the parent form's save flow ships
// to the backend. Pure functions; no React, no I/O.
//
// Tuple format the backend expects:
//   [0, 0, values]      create new child
//   [1, uuid, values]   update existing child
//   [2, uuid, 0]        hard-delete child (O2M default)
//   [3, uuid, 0]        unlink only (M2M)
//   [4, uuid, 0]        link existing record (M2M create-link)
//   [5, 0, 0]           clear all
//   [6, 0, [ids]]       set the relation to this list of ids
//
// PendingOp models the user-visible intent (create / update / delete /
// unlink) keyed by the row identity (tempId for new rows, uuid for
// existing ones). Subsequent edits to the same row MERGE into the
// existing op rather than appending a new one — that keeps the emitted
// command list minimal and avoids backend churn.

export type PendingOp =
    | { kind: "create"; tempId: string; values: Record<string, unknown> }
    | { kind: "update"; uuid: string; values: Record<string, unknown> }
    | { kind: "delete"; uuid: string }
    | { kind: "unlink"; uuid: string };

export type RelationalCommand = [number, unknown, unknown];

/** Stable temp-id generator factory — caller owns the counter so the IDs
 *  stay sequential within a single component instance. */
export function makeTempIdGenerator(): () => string {
    let n = 0;
    return () => `new-${++n}`;
}

/** Serialise a list of pending ops to the backend's tuple format. */
export function opsToCommands(ops: PendingOp[]): RelationalCommand[] {
    return ops.map((op): RelationalCommand => {
        if (op.kind === "create") return [0, 0, op.values];
        if (op.kind === "update") return [1, op.uuid, op.values];
        if (op.kind === "delete") return [2, op.uuid, 0];
        return [3, op.uuid, 0];
    });
}

/** Apply a remove action to the current op list — returns the next list.
 *  Behaviour: if the row is a pending create, drop the create entirely
 *  (it never hits the backend). Otherwise emit `delete` (O2M) or `unlink`
 *  (M2M) depending on `mode`. Any pending update on the same uuid is
 *  dropped — the row's going away anyway. */
export function applyRemove(
    ops: PendingOp[],
    rowId: string,
    mode: "delete" | "unlink",
): PendingOp[] {
    const isPendingCreate = ops.some((o) => o.kind === "create" && o.tempId === rowId);
    if (isPendingCreate) {
        return ops.filter((o) => !(o.kind === "create" && o.tempId === rowId));
    }
    const withoutUpdate = ops.filter((o) => !(o.kind === "update" && o.uuid === rowId));
    const removeOp: PendingOp = mode === "unlink"
        ? { kind: "unlink", uuid: rowId }
        : { kind: "delete", uuid: rowId };
    return [...withoutUpdate, removeOp];
}

/** Apply a create action — appends a new `create` op with the given temp
 *  id and values. The caller is responsible for the temp id. */
export function applyCreate(
    ops: PendingOp[],
    tempId: string,
    values: Record<string, unknown>,
): PendingOp[] {
    return [...ops, { kind: "create", tempId, values }];
}

/** Apply an update action — merges values into a pending create / update
 *  on the same row identity. If neither exists, appends a fresh update
 *  for the existing uuid. */
export function applyUpdate(
    ops: PendingOp[],
    rowId: string,
    values: Record<string, unknown>,
): PendingOp[] {
    const hasPendingCreate = ops.some((o) => o.kind === "create" && o.tempId === rowId);
    if (hasPendingCreate) {
        return ops.map((o) =>
            o.kind === "create" && o.tempId === rowId
                ? { ...o, values: { ...o.values, ...values } }
                : o,
        );
    }
    const hasPendingUpdate = ops.some((o) => o.kind === "update" && o.uuid === rowId);
    if (hasPendingUpdate) {
        return ops.map((o) =>
            o.kind === "update" && o.uuid === rowId
                ? { ...o, values: { ...o.values, ...values } }
                : o,
        );
    }
    return [...ops, { kind: "update", uuid: rowId, values }];
}
